export type ConvertImageOptions = {
    compress?: boolean
    maxSize?: number | never
    format?: 'webp' | 'png' | 'jpeg'
}

export const convertImage = async (
    data: string | Blob | ArrayBuffer | File,
    { compress = false, maxSize, format = 'webp' }: ConvertImageOptions
) => {
    const src = typeof data === 'string' ? data : URL.createObjectURL(data instanceof Blob ? data : new Blob([data]))
    const canvas = document.createElement('canvas')
    const image = new Image()

    try {
        const context = canvas.getContext('2d')
        if (!context) throw new Error('Cannot create an image canvas')
        await new Promise<void>((resolve, reject) => {
            image.onload = () => resolve()
            image.onerror = () => reject(new Error('Cannot load the image'))
            image.crossOrigin = 'anonymous'
            image.src = src
        })

        let width = image.naturalWidth
        let height = image.naturalHeight
        if (!width || !height) throw new Error('The image has no dimensions')
        if (maxSize && (width > maxSize || height > maxSize)) {
            const ratio = Math.min(maxSize / width, maxSize / height)
            width = Math.max(1, Math.floor(width * ratio))
            height = Math.max(1, Math.floor(height * ratio))
        }

        canvas.width = width
        canvas.height = height
        context.drawImage(image, 0, 0, width, height)
        const blob = await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (data) => {
                    if (data) resolve(data)
                    else reject(new Error('Cannot generate the image from canvas'))
                },
                `image/${format}`,
                compress ? 0.8 : 1
            )
        })
        return { blob, width, height }
    } finally {
        image.onload = null
        image.onerror = null
        if (typeof data !== 'string') URL.revokeObjectURL(src)
        image.remove()
        canvas.remove()
    }
}
